import { useState, useEffect } from 'react'
import { Table, Button, Modal, Form, Input, Space, Tag, Popconfirm, message, Select, Row, Col, Checkbox, Divider } from 'antd'
import VoiceTextarea from '@/components/VoiceTextarea'
import PhoneInput from '@/components/PhoneInput'
import { PlusOutlined, EditOutlined, DeleteOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useSuppliers, useCreateSupplier, useUpdateSupplier, useDeleteSupplier } from '@/hooks/useSuppliers'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { usePermission } from '@/hooks/usePermission'
import type { Supplier } from '@/types'

const SuppliersPage = () => {
  const { setTitle } = usePageTitle()
  const canCreate = usePermission('suppliers.create')
  const canEdit   = usePermission('suppliers.edit')
  const canDelete = usePermission('suppliers.delete')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [filterAttivo, setFilterAttivo] = useState<boolean | undefined>(true) // Default: solo attivi
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [form] = Form.useForm()

  useEffect(() => {
    setTitle('Gestione Fornitori')
  }, [setTitle])

  const { data, isLoading } = useSuppliers(page, pageSize, searchText, filterAttivo)
  const createMutation = useCreateSupplier()
  const updateMutation = useUpdateSupplier()
  const deleteMutation = useDeleteSupplier()

  const handleCreate = () => {
    setEditingSupplier(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  const handleEdit = (record: Supplier) => {
    setEditingSupplier(record)
    form.setFieldsValue(record)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id)
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingSupplier) {
        await updateMutation.mutateAsync({ id: editingSupplier.id, data: values })
      } else {
        await createMutation.mutateAsync(values)
        // Reset pagination quando aggiungi un nuovo fornitore
        setPage(1)
        setSearchText('')
      }
      setIsModalOpen(false)
      form.resetFields()
    } catch (error) {
      message.error('Errore durante il salvataggio')
    }
  }

  const columns: ColumnsType<Supplier> = [
    {
      title: 'Codice',
      dataIndex: 'codice',
      key: 'codice',
      width: 80,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Acronimo',
      dataIndex: 'acronimo',
      key: 'acronimo',
      width: 70,
      render: (acronimo) => acronimo ? <Tag>{acronimo}</Tag> : '-',
    },
    {
      title: 'Città',
      dataIndex: 'citta',
      key: 'citta',
      width: 90,
      render: (citta) => citta || '-',
    },
    {
      title: 'Tel',
      dataIndex: 'telefono',
      key: 'telefono',
      width: 60,
      render: (telefono) => (
        telefono ? (
          <a href={`tel:${telefono}`} title={telefono}>
            <PhoneOutlined style={{ color: '#1890ff' }} />
          </a>
        ) : '-'
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 60,
      render: (email) => (
        email ? (
          <a href={`mailto:${email}`} title={email}>
            <MailOutlined style={{ color: '#1890ff' }} />
          </a>
        ) : '-'
      ),
    },
    {
      title: 'Stato',
      dataIndex: 'attivo',
      key: 'attivo',
      width: 80,
      render: (attivo) => (
        <Tag color={attivo ? 'green' : 'red'} style={{ fontSize: 12 }}>
          {attivo ? 'Attivo' : 'Inatt.'}
        </Tag>
      ),
    },
    ...(canEdit || canDelete ? [{
      title: 'Azioni',
      key: 'actions',
      width: 90,
      fixed: 'right' as const,
      render: (_: any, record: Supplier) => (
        <Space size="small">
          {canEdit && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          )}
          {canDelete && (
            <Popconfirm
              title="Sei sicuro?"
              description="Vuoi cancellare questo fornitore?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ]

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <Input.Search
          placeholder="Cerca per codice o nome..."
          onSearch={setSearchText}
          style={{ width: 300 }}
          allowClear
        />
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={filterAttivo}
            onChange={setFilterAttivo}
            style={{ width: 150 }}
            options={[
              { label: 'Solo Attivi', value: true },
              { label: 'Solo Inattivi', value: false },
              { label: 'Tutti', value: undefined },
            ]}
          />
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Nuovo Fornitore
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={data?.items || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          onChange: (p) => setPage(p),
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (_: number, size: number) => { setPageSize(size); setPage(1) },
          showTotal: (total) => `Totale ${total} fornitori`,
        }}
        style={{ marginTop: 24 }}
      />

      <Modal
        title={editingSupplier ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
        open={isModalOpen}
        onOk={form.submit}
        onCancel={() => {
          setIsModalOpen(false)
          form.resetFields()
        }}
        width="min(700px, 95vw)"
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onFinish={handleSubmit}
          style={{ marginTop: 24 }}
        >
          {editingSupplier && (
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item
                  label="Codice"
                  name="codice"
                  rules={[
                    { required: true, message: 'Obbligatorio' },
                  ]}
                >
                  <Input disabled placeholder="Auto-generato" size="small" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label="Nome"
                  name="nome"
                  rules={[
                    { required: true, message: 'Obbligatorio' },
                  ]}
                >
                  <Input placeholder="AutoParts Italia" size="small" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Acronimo"
                  name="acronimo"
                  rules={[{ max: 20, message: 'Troppo lungo' }]}
                >
                  <Input placeholder="API" size="small" />
                </Form.Item>
              </Col>
            </Row>
          )}
          
          {!editingSupplier && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label="Nome"
                  name="nome"
                  rules={[
                    { required: true, message: 'Obbligatorio' },
                  ]}
                >
                  <Input placeholder="AutoParts Italia" size="small" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Acronimo"
                  name="acronimo"
                  rules={[{ max: 20, message: 'Troppo lungo' }]}
                >
                  <Input placeholder="API" size="small" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                label="P.IVA"
                name="partita_iva"
                rules={[{ max: 20, message: 'Troppo lunga' }]}
              >
                <Input placeholder="12345678901" size="small" autoComplete="one-time-code" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Codice Fiscale"
                name="codice_fiscale"
                rules={[{ max: 20, message: 'Troppo lungo' }]}
              >
                <Input placeholder="ABCDEF12G1H2I901" size="small" autoComplete="one-time-code" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Telefono"
                name="telefono"
              >
                <PhoneInput size="small" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { max: 100, message: 'Troppo lunga' },
                  { type: 'email', message: 'Non valida' },
                ]}
              >
                <Input placeholder="info@supplier.it" size="small" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Indirizzo"
                name="indirizzo"
                rules={[{ max: 255, message: 'Troppo lungo' }]}
              >
                <Input placeholder="Via/Piazza" size="small" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                label="Città"
                name="citta"
                rules={[{ max: 100, message: 'Troppo lunga' }]}
              >
                <Input placeholder="Roma" size="small" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Provincia"
                name="provincia"
                rules={[{ max: 50, message: 'Troppo lunga' }]}
              >
                <Input placeholder="RM" maxLength={2} size="small" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="CAP"
                name="cap"
                rules={[{ max: 5, message: 'Troppo lungo' }]}
              >
                <Input placeholder="00100" maxLength={5} size="small" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="Referente Acquisti"
                name="referente_acquisti"
                rules={[{ max: 100, message: 'Troppo lungo' }]}
              >
                <Input placeholder="Mario Rossi" size="small" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Note Commerciali"
                name="note_commerciali"
                rules={[{ max: 500, message: 'Troppo lunghe' }]}
              >
                <VoiceTextarea rows={2} minHeight={70} maxHeight={150} placeholder="Sconti, condizioni, etc." label="Note commerciali" compact />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0' }} />

          <Form.Item
            name="attivo"
            valuePropName="checked"
            initialValue={!editingSupplier ? true : editingSupplier.attivo}
          >
            <Checkbox>Fornitore Attivo</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SuppliersPage
